import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CLASS_GROUP_OPTIONS } from '../../school-constants';
import { Student } from '../../student';
import { StudentService } from '../../services/student.service';

export type StudentDialogMode = 'create' | 'edit' | 'view';

export interface StudentDialogData {
  mode: StudentDialogMode;
  student?: Student;
}

@Component({
  selector: 'app-student-dialog',
  templateUrl: './student-dialog.component.html',
  styleUrls: ['./student-dialog.component.css']
})
export class StudentDialogComponent implements OnInit {

  form!: FormGroup;
  title: string;

  readonly classOptions = [...CLASS_GROUP_OPTIONS];

  readonly genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' }
  ];

  readonly bloodGroupOptions = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<StudentDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: StudentDialogData,
    private studentService: StudentService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    const s = this.data.student;
    const isView = this.data.mode === 'view';
    const isCreate = this.data.mode === 'create';

    this.title =
      this.data.mode === 'create' ? 'Create student' :
      this.data.mode === 'edit' ? 'Edit student' : 'Student details';

    const nameValidators = isView ? [] : [Validators.required];
    const pincodeInit = s?.addressDto?.pincode != null ? String(s.addressDto.pincode) : '';
    const dobInit = this.parseDob(s?.dateOfBirth);

    const dobValidators = isView ? [] : [Validators.required];

    this.form = this.fb.group({
      rollNumber: [{ value: isCreate ? '' : (s?.rollNumber ?? ''), disabled: true }],
      firstName: [{ value: s?.firstName ?? '', disabled: isView }, nameValidators],
      lastName: [{ value: s?.lastName ?? '', disabled: isView }, nameValidators],
      dateOfBirth: [{ value: dobInit, disabled: isView }, dobValidators],
      gender: [{ value: s?.gender ?? '', disabled: isView }, [Validators.required]],
      fatherName: [{ value: s?.fatherName ?? '', disabled: isView }],
      motherName: [{ value: s?.motherName ?? '', disabled: isView }],
      mobileNumber: [{ value: s?.mobileNumber != null ? String(s.mobileNumber) : '', disabled: isView }],
      email: [{ value: s?.email ?? '', disabled: isView }, Validators.email],
      bloodGroup: [{ value: s?.bloodGroup ?? '', disabled: isView }],
      className: [{ value: s?.className ?? '', disabled: isView }, [Validators.required]],
      addressDto: this.fb.group({
        doorNo: [{ value: s?.addressDto?.doorNo ?? '', disabled: isView }],
        city: [{ value: s?.addressDto?.city ?? '', disabled: isView }],
        state: [{ value: s?.addressDto?.state ?? '', disabled: isView }],
        country: [{ value: s?.addressDto?.country ?? '', disabled: isView }],
        pincode: [{ value: pincodeInit, disabled: isView }]
      })
    });
  }

  private parseDob(raw?: string): Date | null {
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  isView(): boolean {
    return this.data.mode === 'view';
  }

  isCreate(): boolean {
    return this.data.mode === 'create';
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private formatDob(d: Date | null): string | undefined {
    if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
      return undefined;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  submit(): void {
    if (this.data.mode === 'view' || this.form.invalid) {
      return;
    }

    const raw = this.form.getRawValue();
    const pin = raw.addressDto.pincode;
    const bg = (raw.bloodGroup || '').trim();
    const dob = this.formatDob(raw.dateOfBirth);

    const student: Student = {
      firstName: raw.firstName,
      lastName: raw.lastName,
      gender: raw.gender,
      fatherName: raw.fatherName,
      motherName: raw.motherName,
      mobileNumber: raw.mobileNumber ? Number(raw.mobileNumber) : undefined,
      email: raw.email,
      bloodGroup: bg || undefined,
      className: raw.className,
      dateOfBirth: dob,
      addressDto: {
        ...this.data.student?.addressDto,
        doorNo: raw.addressDto.doorNo,
        city: raw.addressDto.city,
        state: raw.addressDto.state,
        country: raw.addressDto.country,
        pincode: pin ? Number(pin) : undefined
      }
    };

    if (this.data.mode === 'edit') {
      student.id = this.data.student?.id;
      student.rollNumber = raw.rollNumber;
      const id = this.data.student?.id;
      if (id == null) {
        return;
      }
      this.studentService.update(id, student).subscribe(
        () => {
          this.snackBar.open('Student saved', 'Dismiss', { duration: 3000 });
          this.dialogRef.close(true);
        },
        () => {
          this.snackBar.open('Could not save student. Check API or network.', 'Dismiss', { duration: 5000 });
        }
      );
      return;
    }

    this.studentService.create(student).subscribe(
      () => {
        this.snackBar.open('Student created (no login until provisioned on server)', 'Dismiss', { duration: 4000 });
        this.dialogRef.close(true);
      },
      () => {
        this.snackBar.open('Could not create student. Check API or network.', 'Dismiss', { duration: 5000 });
      }
    );
  }
}
